package com.eladfinish.bible.contest.screens.main.featureSheet

import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.repository.remoteConfig.RemoteConfig
import com.eladfinish.bible.contest.repository.remoteConfig.RemoteConfigRepository
import com.eladfinish.bible.contest.repository.remoteConfig.get
import com.eladfinish.bible.contest.screens.main.ButtonListOrderRepository
import com.russhwolf.settings.Settings

/**
 * Who sees a one-time "what's new" sheet that introduces a feature: users who had the app before
 * the version that introduced the sheet, once, on the platforms that have the feature.
 *
 * "Had the app before" is decided at the first launch of a version that has the sheet
 * ([captureExistingUser]) and kept, so a new user is still new at their second launch, after the
 * gender dialog of the first one has set the flag this reads.
 *
 * [switchesThatMustBeOff] are negative Remote Config switches (the feature's own and the sheet's
 * own); a missing parameter or a failed read means on.
 */
abstract class OneTimeSheetGate(
    private val settings: Settings,
    private val remoteConfigRepository: RemoteConfigRepository,
    private val isSupportedPlatform: Boolean,
    private val existingUserKey: String,
    private val shownKey: String,
    private val switchesThatMustBeOff: List<RemoteConfig>,
) {

    /**
     * Records once whether this user had the app before this version: they answered the gender
     * dialog every first launch has shown since 2023 (it can't be dismissed), or they saved a
     * button order. Must run before this launch's gender dialog can be answered.
     */
    fun captureExistingUser() {
        if (settings.hasKey(existingUserKey)) return
        val answeredGenderDialog = settings.getBoolean(Constants.PREF_HEBREW_GENDER_LANGUAGE_SET, false)
        val savedButtonOrder = settings.getString(ButtonListOrderRepository.KEY, "[]").let { it.isNotBlank() && it != "[]" }
        settings.putBoolean(existingUserKey, answeredGenderDialog || savedButtonOrder)
    }

    /** Remote Config is read on every call, so a switch fetched after launch is respected. */
    fun shouldShow(): Boolean =
        isSupportedPlatform &&
            settings.getBoolean(existingUserKey, false) &&
            !settings.getBoolean(shownKey, false) &&
            switchesThatMustBeOff.all { remoteConfigRepository.isOnUnlessDisabled(it) }

    /** Called as the sheet appears, not when it closes: a crash or a kill must not show it again. */
    fun markShown() {
        settings.putBoolean(shownKey, true)
    }
}

/**
 * The sheet to offer at this launch: none while the gender dialog or a system prompt is due, else
 * the first of [gates] (in priority order) that wants to show. One sheet per launch: the others
 * wait for the next one rather than queueing up back to back.
 */
fun firstSheetDue(isStartupPromptDue: Boolean, gates: List<OneTimeSheetGate>): OneTimeSheetGate? =
    if (isStartupPromptDue) null else gates.firstOrNull { it.shouldShow() }

/** A negative Remote Config switch: a missing parameter or a failed read means on. */
internal fun RemoteConfigRepository.isOnUnlessDisabled(disableSwitch: RemoteConfig): Boolean =
    runCatching { get<Boolean>(disableSwitch) }.getOrNull() != true
